import { Core } from '@pdftron/webviewer';

export const MODES = {
  EDIT: 'EDIT',
  VIEW: 'VIEW',
} as const;

export const ANNOTATION_CHANGE_EVENT = {
  ADD: 'ADD',
  MODIFY: 'MODIFY',
} as const;

export type Scale = {
  scaleFromDimension: number;
  scaleFromUnit: string;
  scaleToDimension: number;
  scaleToUnit: string;
};

export const DEFAULT_SCALE: Scale = {
  scaleFromDimension: 0.25,
  scaleFromUnit: 'in',
  scaleToDimension: 1,
  scaleToUnit: 'in',
};

export const ANNOTATIONS_TOOL_NAMES = [
  'AnnotationCreateArrow',
  'AnnotationCreateArrow2',
  'AnnotationCreateArrow3',
  'AnnotationCreateArrow4',
  'AnnotationCreateCallout',
  'AnnotationCreateCallout2',
  'AnnotationCreateCallout3',
  'AnnotationCreateCallout4',
  'AnnotationCreateEllipse',
  'AnnotationCreateEllipse2',
  'AnnotationCreateEllipse3',
  'AnnotationCreateEllipse4',
  'AnnotationCreateFreeHand',
  'AnnotationCreateFreeHand2',
  'AnnotationCreateFreeHand3',
  'AnnotationCreateFreeHand4',
  'AnnotationCreateFreeText',
  'AnnotationCreateFreeText2',
  'AnnotationCreateFreeText3',
  'AnnotationCreateFreeText4',
  'AnnotationCreateLine',
  'AnnotationCreateLine2',
  'AnnotationCreateLine3',
  'AnnotationCreateLine4',
  'AnnotationCreatePolygon',
  'AnnotationCreatePolygon2',
  'AnnotationCreatePolygon3',
  'AnnotationCreatePolygon4',
  'AnnotationCreatePolygonCloud',
  'AnnotationCreatePolygonCloud2',
  'AnnotationCreatePolygonCloud3',
  'AnnotationCreatePolygonCloud4',
  'AnnotationCreatePolyline',
  'AnnotationCreatePolyline2',
  'AnnotationCreatePolyline3',
  'AnnotationCreatePolyline4',
  'AnnotationCreateRectangle',
  'AnnotationCreateRectangle2',
  'AnnotationCreateRectangle3',
  'AnnotationCreateRectangle4',
  'AnnotationCreateSticky',
  'AnnotationCreateSticky2',
  'AnnotationCreateSticky3',
  'AnnotationCreateSticky4',
  'AnnotationCreateTextHighlight',
  'AnnotationCreateTextHighlight2',
  'AnnotationCreateTextHighlight3',
  'AnnotationCreateTextHighlight4',
  'AnnotationCreateTextSquiggly',
  'AnnotationCreateTextSquiggly2',
  'AnnotationCreateTextSquiggly3',
  'AnnotationCreateTextSquiggly4',
  'AnnotationCreateTextStrikeout',
  'AnnotationCreateTextStrikeout2',
  'AnnotationCreateTextStrikeout3',
  'AnnotationCreateTextStrikeout4',
  'AnnotationCreateTextUnderline',
  'AnnotationCreateTextUnderline2',
  'AnnotationCreateTextUnderline3',
  'AnnotationCreateTextUnderline4',
] as const;

export const UNIVERSALLY_DISABLED_ELEMENTS = [
  'annotationRedactButton',
  'polygonCloudToolGroupButton',
  'fullscreenButton',
  'linkButton',
  'startFormEditingToolGroupButton',
  'toolbarGroup-Insert',
  'toolbarGroup-FillAndSign',
  'toolbarGroup-Edit',
  'toolbarGroup-Forms',
  'redactionPanelToggle', // Remove redaction panel toggle UI button
  'redactionPanel', // Disable the redaction panel -- allows us to use custom redaction 'save' without UI confusion
  'thumbDelete',
  'thumbMultiDelete',
  'measurementSnappingOption',
  'thumbnailsSizeSlider',
  'annotationContentOverlay',
  'modalRedactButton',
  'toggleCompareModeButton',
  'customPageManipulationPrint', // Remove 'Replace' from thumbnail context menu
  'customPageManipulationDelete', // Remove 'Delete' from thumbnail context menu
  'outlinesPanel-tabPanel',
  'bookmarksPanel-tabPanel',
  'layersPanel-tabPanel',
  'signaturePanel-tabPanel',
  'fileAttachmentPanel-tabPanel',
] as const;

export const STANDALONE_DISABLED_ELEMENTS = [
  'toolbarGroup-Annotate',
  'toolbarGroup-Shapes',
  'toolbarGroup-Redact',
  'toolbarGroup-Measure',
  'toolbarGroup-View',
  'tools-header',
] as const;

export const MEASUREMENT_TOOL_NAMES = (Tools: typeof Core.Tools) => [
  Tools.ToolNames.DISTANCE_MEASUREMENT,
  Tools.ToolNames.DISTANCE_MEASUREMENT2,
  Tools.ToolNames.DISTANCE_MEASUREMENT3,
  Tools.ToolNames.DISTANCE_MEASUREMENT4,
  Tools.ToolNames.PERIMETER_MEASUREMENT,
  Tools.ToolNames.PERIMETER_MEASUREMENT2,
  Tools.ToolNames.PERIMETER_MEASUREMENT3,
  Tools.ToolNames.PERIMETER_MEASUREMENT4,
  Tools.ToolNames.AREA_MEASUREMENT,
  Tools.ToolNames.AREA_MEASUREMENT2,
  Tools.ToolNames.AREA_MEASUREMENT3,
  Tools.ToolNames.AREA_MEASUREMENT4,
  Tools.ToolNames.RECTANGULAR_AREA_MEASUREMENT,
  Tools.ToolNames.RECTANGULAR_AREA_MEASUREMENT2,
  Tools.ToolNames.RECTANGULAR_AREA_MEASUREMENT3,
  Tools.ToolNames.RECTANGULAR_AREA_MEASUREMENT4,
  Tools.ToolNames.ELLIPSE_MEASUREMENT,
  Tools.ToolNames.ELLIPSE_MEASUREMENT2,
  Tools.ToolNames.ELLIPSE_MEASUREMENT3,
  Tools.ToolNames.ELLIPSE_MEASUREMENT4,
  Tools.ToolNames.ARC_MEASUREMENT,
  Tools.ToolNames.ARC_MEASUREMENT2,
  Tools.ToolNames.ARC_MEASUREMENT3,
  Tools.ToolNames.ARC_MEASUREMENT4,
];

export const TOOLS_WITH_FILL = (Tools: typeof Core.Tools) => [
  Tools.ToolNames.RECTANGLE,
  Tools.ToolNames.RECTANGLE2,
  Tools.ToolNames.RECTANGLE3,
  Tools.ToolNames.RECTANGLE4,
  Tools.ToolNames.ELLIPSE,
  Tools.ToolNames.ELLIPSE2,
  Tools.ToolNames.ELLIPSE3,
  Tools.ToolNames.ELLIPSE4,
  Tools.ToolNames.POLYGON,
  Tools.ToolNames.POLYGON2,
  Tools.ToolNames.POLYGON3,
  Tools.ToolNames.POLYGON4,
  Tools.ToolNames.AREA_MEASUREMENT,
  Tools.ToolNames.AREA_MEASUREMENT2,
  Tools.ToolNames.AREA_MEASUREMENT3,
  Tools.ToolNames.AREA_MEASUREMENT4,
  Tools.ToolNames.RECTANGULAR_AREA_MEASUREMENT,
  Tools.ToolNames.RECTANGULAR_AREA_MEASUREMENT2,
  Tools.ToolNames.RECTANGULAR_AREA_MEASUREMENT3,
  Tools.ToolNames.RECTANGULAR_AREA_MEASUREMENT4,
  Tools.ToolNames.ELLIPSE_MEASUREMENT,
  Tools.ToolNames.ELLIPSE_MEASUREMENT2,
  Tools.ToolNames.ELLIPSE_MEASUREMENT3,
  Tools.ToolNames.ELLIPSE_MEASUREMENT4,
];

export const REDACT_SUBJECT = 'Redact';
